import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useTier } from './TierContext';

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
  const { api, user, token } = useAuth();
  const { hasFeature } = useTier();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Check if messaging feature is available
  const canUseChat = hasFeature('messaging');

  // Fetch chat rooms
  const fetchRooms = useCallback(async () => {
    if (!api || !canUseChat) return;
    
    try {
      const response = await api.get('/chat/rooms');
      setRooms(response.data);
      
      // Calculate unread count
      const unread = response.data.reduce((acc, room) => {
        return acc + (room.unread_count || 0);
      }, 0);
      setUnreadCount(unread);
    } catch (err) {
      console.error('Failed to fetch chat rooms:', err);
    }
  }, [api, canUseChat]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (roomId) => {
    if (!api || !roomId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/chat/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Connect to WebSocket for a room
  const connectToRoom = useCallback((roomId) => {
    if (!token || !roomId || !canUseChat) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Build WebSocket URL - handle both local and production environments
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    
    if (backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1')) {
      // Local development - connect directly to backend
      wsUrl = `ws://localhost:8001/ws/chat/${roomId}?token=${token}`;
    } else {
      // Production - use the same host as the backend URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      wsUrl = `${wsProtocol}//${wsHost}/ws/chat/${roomId}?token=${token}`;
    }
    
    if (process.env.NODE_ENV === 'development') console.log('Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      if (process.env.NODE_ENV === 'development') console.log('WebSocket connected to room:', roomId);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message') {
          setMessages(prev => [...prev, data]);
          
          // Update room's last message in the list
          setRooms(prev => prev.map(room => 
            room.id === roomId 
              ? { ...room, last_message: data.content, last_message_at: data.created_at }
              : room
          ));
        } else if (data.type === 'user_joined' || data.type === 'user_left') {
          // Could show a system message
          setMessages(prev => [...prev, {
            id: `system-${Date.now()}`,
            type: 'system',
            content: `${data.user_name} ${data.type === 'user_joined' ? 'joined' : 'left'} the chat`,
            created_at: data.timestamp
          }]);
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
      }
    };
    
    ws.onclose = (event) => {
      if (process.env.NODE_ENV === 'development') console.log('WebSocket closed:', event.code, event.reason);
      // Attempt to reconnect after 3 seconds if not intentionally closed
      if (event.code !== 1000 && activeRoom?.id === roomId) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectToRoom(roomId);
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  }, [token, canUseChat, activeRoom]);

  // Send a message via WebSocket
  const sendMessage = useCallback(async (content, messageType = 'text', jobId = null) => {
    if (!activeRoom) return;
    
    const messageData = {
      type: 'message',
      content,
      message_type: messageType,
      job_id: jobId
    };
    
    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(messageData));
    } else {
      // Fallback to REST API
      try {
        const response = await api.post(`/chat/rooms/${activeRoom.id}/messages`, {
          content,
          message_type: messageType,
          job_id: jobId
        });
        // Add the message to local state immediately
        setMessages(prev => [...prev, response.data]);
      } catch (err) {
        console.error('Failed to send message:', err);
        throw err;
      }
    }
  }, [activeRoom, api]);

  // Send typing indicator
  const sendTypingIndicator = useCallback((isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }));
    }
  }, []);

  // Polling for new messages (fallback when WebSocket is unreliable)
  const pollIntervalRef = useRef(null);
  
  const startPolling = useCallback((roomId) => {
    // Poll every 3 seconds for new messages
    pollIntervalRef.current = setInterval(async () => {
      if (!api || !roomId) return;
      try {
        const response = await api.get(`/chat/rooms/${roomId}/messages`);
        setMessages(response.data);
      } catch (err) {
        console.error('Polling failed:', err);
      }
    }, 3000);
  }, [api]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Open a specific room
  const openRoom = useCallback(async (room) => {
    // If room is null, just go back to room list
    if (!room) {
      stopPolling();
      setActiveRoom(null);
      setMessages([]);
      fetchRooms();
      return;
    }
    
    // Stop any existing polling/connections before switching rooms
    stopPolling();
    
    // Clear messages immediately to prevent cross-room contamination
    setMessages([]);
    
    // Set new active room
    setActiveRoom(room);
    setIsOpen(true);
    
    // Fetch messages for new room
    await fetchMessages(room.id);
    
    // Connect WebSocket and start polling for new room
    connectToRoom(room.id);
    startPolling(room.id);
  }, [fetchMessages, connectToRoom, startPolling, stopPolling, fetchRooms]);

  // Create a new room
  const createRoom = useCallback(async (name, roomType = 'general', jobId = null, participantIds = []) => {
    if (!api) return null;
    
    try {
      const response = await api.post('/chat/rooms', {
        name,
        room_type: roomType,
        job_id: jobId,
        participant_ids: participantIds
      });
      
      await fetchRooms();
      return response.data;
    } catch (err) {
      console.error('Failed to create room:', err);
      throw err;
    }
  }, [api, fetchRooms]);

  // Get or create job chat room
  const openJobChat = useCallback(async (jobId) => {
    if (!api) return null;
    
    try {
      const response = await api.get(`/chat/job/${jobId}/room`);
      openRoom(response.data);
      return response.data; // Return the room so caller can use it
    } catch (err) {
      console.error('Failed to get job chat room:', err);
      return null;
    }
  }, [api, openRoom]);

  // Close chat panel
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setActiveRoom(null);
    setMessages([]);
    
    // Stop polling
    stopPolling();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User closed chat');
      wsRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, [stopPolling]);

  // Fetch rooms on mount
  useEffect(() => {
    if (user && canUseChat) {
      fetchRooms();
    }
  }, [user, canUseChat, fetchRooms]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [stopPolling]);

  const value = {
    rooms,
    activeRoom,
    messages,
    isOpen,
    loading,
    unreadCount,
    canUseChat,
    fetchRooms,
    openRoom,
    closeChat,
    sendMessage,
    sendTypingIndicator,
    createRoom,
    openJobChat,
    setIsOpen
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;
